import type {
  AuditEntryDTO,
  DashboardDTO,
  KbDocDTO,
  KbDocDetailDTO,
  KbStatsDTO,
  Paginated,
  Settings,
  UsageRowDTO,
} from '@copilot/shared';
import { api, downloadFile } from './client';
import type { KbDocBody, KbDocPatch } from './types';

const enc = encodeURIComponent;

export const dashboardApi = {
  get: (from: string, to: string) => api<DashboardDTO>('/dashboard', { query: { from, to } }),
};

export const usageApi = {
  get: (month: string) => api<UsageRowDTO[]>('/usage', { query: { month } }),
  exportCsv: (month: string) => downloadFile('/usage.csv', `usage-${month}.csv`, { month }),
};

export const kbApi = {
  list: () => api<KbDocDTO[]>('/kb/docs'),
  stats: () => api<KbStatsDTO>('/kb/stats'),
  get: (id: string) => api<KbDocDetailDTO>(`/kb/docs/${enc(id)}`),
  create: (body: KbDocBody) => api<KbDocDTO>('/kb/docs', { method: 'POST', body }),
  upload: (file: File, title: string, category: string) => {
    const form = new FormData();
    // Text fields first so a streaming multipart parser sees them before the file.
    form.append('title', title);
    form.append('category', category);
    form.append('file', file);
    return api<KbDocDTO>('/kb/docs/upload', { method: 'POST', body: form });
  },
  patch: (id: string, body: KbDocPatch) => api<KbDocDTO>(`/kb/docs/${enc(id)}`, { method: 'PATCH', body }),
  restore: (id: string, version: number) =>
    api<KbDocDTO>(`/kb/docs/${enc(id)}/restore`, { method: 'POST', body: { version } }),
  remove: (id: string) => api<void>(`/kb/docs/${enc(id)}`, { method: 'DELETE' }),
};

export const settingsApi = {
  get: () => api<Settings>('/settings'),
  put: (body: Settings) => api<Settings>('/settings', { method: 'PUT', body }),
};

export const auditApi = {
  list: (params: { page: number; pageSize: number; entityType?: string; actorId?: string }) =>
    api<Paginated<AuditEntryDTO>>('/audit', { query: params }),
};
